import React, { useState } from "react";
import { useMutation, useQuery } from "react-query";
import { Formik, Form } from "formik";
import { useAuthState, useAuthDispatch } from "context";
import { apiFetch, formatUiErrors } from "utils";
import Button from "components/Button";
import moment from "moment";
import * as Yup from "yup";
import { endOfMonth } from "date-fns";
import DatePickerField from "components/DatePickerField";
import FormField from "components/FormField";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faSpinner,
  faExclamationCircle,
} from "@fortawesome/free-solid-svg-icons";

const ReaggregationSchema = Yup.object().shape({
  date: Yup.date().required("Date is required"),
  location_id: Yup.string().nullable(),
});

function ShelterBuddyReaggregation() {
  const { token } = useAuthState();
  const dispatch = useAuthDispatch();

  const [currentJobId, setCurrentJobId] = useState(null);
  const [finalStatus, setFinalStatus] = useState(null);
  const [finalError, setFinalError] = useState(null);

  const { data: locationsData, isLoading: isLoadingLocations } = useQuery(
    ["shelterbuddyReaggregationLocations"],
    () =>
      apiFetch({
        token,
        dispatch,
        method: "GET",
        endpoint: "/api/v1/shelterbuddy_location_mappings",
      })
  );

  const locations = locationsData?.data?.locations ?? [];
  const hasMappedLocations = locations.length > 0;
  const hasSingleLocation = locations.length === 1;

  const { mutate: reaggregateData, isLoading: isQueueing } = useMutation(
    ({ date, location_id }) => {
      const parsedDate = moment(date);
      const payload = {
        month: parsedDate.format("M"),
        year: parsedDate.format("YYYY"),
        ...(location_id ? { location_id } : {}),
      };

      return apiFetch({
        token,
        dispatch,
        method: "POST",
        endpoint: "/api/internal/shelterbuddy/shelterbuddy_reaggregations",
        body: payload,
      });
    },
    {
      onSuccess: (response) => {
        const jobId = response?.data?.jobId;

        if (jobId) {
          setCurrentJobId(jobId);
          toast.info("Job queued. Processing...", { autoClose: 2000 });
        } else {
          setFinalStatus("failed");
          setFinalError("Unable to retrieve job ID from server");
          toast.error("Failed to start reaggregation job");
        }
      },
      onError: (err) => {
        const errorMessage = formatUiErrors(err);
        setFinalStatus("failed");
        setFinalError(errorMessage);
        toast.error(errorMessage, { autoClose: 5000 });
      },
    }
  );

  useQuery(
    ["shelterbuddyReaggregationStatus", currentJobId],
    () =>
      apiFetch({
        token,
        dispatch,
        method: "GET",
        endpoint: `/api/internal/shelterbuddy/shelterbuddy_reaggregations/${currentJobId}`,
      }),
    {
      enabled: !!currentJobId && !finalStatus,
      refetchInterval: 2000,
      onSuccess: (data) => {
        const status = data?.data?.status;

        if (status === "completed") {
          setFinalStatus("success");
          setCurrentJobId(null);
          toast.success("Reaggregation completed successfully!");
        } else if (status === "failed") {
          setFinalStatus("failed");
          setFinalError(data?.data?.errorMessage || "An error occurred");
          setCurrentJobId(null);
          toast.error("Reaggregation failed");
        }
      },
      onError: () => {
        setFinalStatus("failed");
        setFinalError("Unable to check job status");
        setCurrentJobId(null);
        toast.error("Error checking job status");
      },
    }
  );

  const isSuccess = finalStatus === "success";
  const isFailed = finalStatus === "failed";
  const isProcessing =
    (isQueueing || !!currentJobId) && !isSuccess && !isFailed;

  const handleSubmit = (values, { setSubmitting }) => {
    setFinalStatus(null);
    setFinalError(null);
    setCurrentJobId(null);
    reaggregateData(values);
    setSubmitting(false);
  };

  return (
    <div className="max-w-3xl mx-auto p-6">
      <h1 className="mb-4 text-2xl font-bold">Reaggregation Tool</h1>

      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded text-blue-800">
        <p>
          This tool recalculates your monthly counts using the data currently in
          your account. Use this after updating your mapping or after completing
          a resync. Select the month you'd like to reaggregate, and we'll
          rebuild the totals for that month.
        </p>
      </div>

      <div className="bg-white rounded p-6 border shadow-sm">
        <Formik
          initialValues={{ date: null, location_id: "" }}
          validationSchema={ReaggregationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ isValid, dirty }) => (
            <Form>
              {!isLoadingLocations && hasMappedLocations && (
                <div className="mb-6">
                  <FormField
                    name="location_id"
                    label="Select Location"
                    as="select"
                  >
                    <option value="">
                      {hasSingleLocation ? "-- Choose --" : "All Locations"}
                    </option>
                    {locations.map((location) => (
                      <option value={location.id} key={location.id}>
                        {location.name}
                      </option>
                    ))}
                  </FormField>
                </div>
              )}

              <div className="mb-6">
                <DatePickerField
                  label="Select Month"
                  name="date"
                  dateFormat="MMMM yyyy"
                  showMonthYearPicker
                  className="form-input w-full"
                  maxDate={endOfMonth(new Date())}
                  required
                  placeholderText="Ex: April 2025"
                />
              </div>

              <div className="flex items-center justify-between mt-8">
                <div className="flex items-center min-h-[24px] flex-1 mr-4">
                  {isProcessing && (
                    <div className="flex items-center text-blue-600">
                      <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                      <span>Reaggregating... please wait.</span>
                    </div>
                  )}
                  {isSuccess && (
                    <div className="flex items-center text-green-600">
                      <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
                      <span>
                        Reaggregation complete. Your counts for this month have
                        been updated.
                      </span>
                    </div>
                  )}
                  {isFailed && (
                    <div className="flex items-center text-red-600">
                      <FontAwesomeIcon
                        icon={faExclamationCircle}
                        className="mr-2"
                      />
                      <span className="text-sm">
                        {finalError || "An unexpected error occurred."}
                      </span>
                    </div>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={!isValid || !dirty || isProcessing}
                  isLoading={isProcessing}
                  loadingText="Processing..."
                >
                  Reaggregate Month
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
}

export default ShelterBuddyReaggregation;
